<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	// Svelte
	import { getContext } from 'svelte';
	// Services
	import { format } from 'svelte-i18n';
	// Design system components
	import { Stack, ListItem, Text, Toggle, SystemStyles } from '@Shelly/shelly-design-system';
	// Helpers
	import { capFirstChar } from '../../helpers/utilities';
	import { scrollToAdditionalContent } from '../Lists/list-helpers';
	//Types
	import type { ChartDataInterface, ChartStatisticInterface } from '../../types/Chart';
	import TransitionWrapper from '../Utility/TransitionWrapper.svelte';
	import { isDistance, real_id } from 'src/helpers/interface_helpers';
	import { devices } from 'src/stores/lists/devices';
	import { add118mmToStats, translateDistanceUnits } from 'src/helpers/distance_helper';

	$: settingsStore = getDeviceSettings(device.id);
	export let chartData: ChartDataInterface;
	export let chartStatistic: ChartStatisticInterface;
	export let showTariffs = false;
	export let fullScreenDeviceChartObj = null;

	const device = $devices[chartData?.deviceID];

	let additionalContentElRef: HTMLElement = null;

	let deviceDrawerContainerRef: SvelteStore<HTMLElement> = getContext('deviceDrawerContainer');

	$: if (additionalContentElRef) {
		if (fullScreenDeviceChartObj?.fullScreen) {
			scrollToAdditionalContent(
				additionalContentElRef,
				fullScreenDeviceChartObj['fullScreenTarget'],
			);
		} else {
			scrollToAdditionalContent(additionalContentElRef, $deviceDrawerContainerRef);
		}
	}

	if (isDistance(chartData?.deviceType)) {
		const distanceUnit =
			device?.ht_ui?.unit || $settingsStore?.ht_ui?.unit || 'mm';
		chartStatistic = add118mmToStats(chartStatistic, distanceUnit, device?.measureFromBase);
	}

	chartStatistic = translateDistanceUnits(chartStatistic, device?.type);
</script>

{#if chartStatistic && chartStatistic.available}
	{#if chartStatistic.phaseName}
		<Text class="mt-m mb-s ml-xs">{$format(`chart_legend_${chartStatistic.phaseName}`)}</Text>
	{/if}
	<Stack gap="xs" class="mt-s">
		{#if chartStatistic.totalConsumedValue > 0}
			{#if chartStatistic.comparisonDiff !== null}
				<ListItem
					icon={'fas fa-leaf'}
					title={capFirstChar(
						$format('list_item_title_eco_consumption', {
							default: 'eco',
						}),
					)}
					subtitle={'(' +
						$format('list_item_subtitle_compared', {
							default: 'Compared',
						}) +
						')'}
					progress={{
						title: `${
							chartStatistic.comparison
								? $format('chart_consumption_comparison_lower', {
										default: `${
											chartStatistic.comparisonDiff > 200
												? $format('value_a_lot', { default: 'a lot' })
												: chartStatistic.comparisonDiff + '%'
										} lower`,
										values: {
											value:
												chartStatistic.comparisonDiff > 200
													? $format('value_a_lot', { default: 'a lot' })
													: chartStatistic.comparisonDiff + '%',
										},
									})
								: $format('chart_consumption_comparison_higher', {
										default: `${
											chartStatistic.comparisonDiff > 200
												? $format('value_a_lot', { default: 'a lot' })
												: chartStatistic.comparisonDiff + '%'
										} higher`,
										values: {
											value:
												chartStatistic.comparisonDiff > 200
													? $format('value_a_lot', { default: 'a lot' })
													: chartStatistic.comparisonDiff + '%',
										},
									})
						}`,
						color: chartStatistic.comparison ? 'success' : 'danger',
					}}
					viewType="default"
				/>
			{/if}
			<ListItem
				icon={'fas fa-plus'}
				title={capFirstChar(
					$format('text_total_energy', {
						default: 'total energy',
					}),
				)}
				extraText={{
					title: `${chartStatistic.totalConsumed}`,
					color: 'primary',
					icon: 'fas fa-bolt',
					iconColor: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,
				}}
				progress={{
					title: `${chartStatistic.totalCost ? chartStatistic.totalCost : ''}`,
					color: 'warning',
					icon: `${chartStatistic.totalCost ? 'fas fa-coins' : ''}`,
					iconColor: SystemStyles.COLOR_CHART_MONEY_VALUE,
				}}
			/>
		{/if}
		{#if chartStatistic.totalGeneratedValue > 0}
			<ListItem
				icon={'fas fa-minus'}
				title={capFirstChar(
					$format('label_total_returned', {
						default: 'total Returned',
					}),
				)}
				progress={{
					title: chartStatistic.totalGenerated,
					color: 'solar',
					icon: 'fas fa-bolt',
					iconColor: SystemStyles.COLOR_SOLAR,
				}}
			/>
		{/if}
		{#if chartStatistic.tariffs?.length > 0}
			<Toggle id="toggle-show-tariffs-distribution" bind:checked={showTariffs} size="m" on:click>
				{capFirstChar(
					$format('toggle_chart_stats_tariff_distribution', {
						default: 'show tariffs distribution',
					}),
				)}
			</Toggle>
			{#if showTariffs}
				<div bind:this={additionalContentElRef}>
					<TransitionWrapper>
						<Stack gap="xs">
							{#each chartStatistic.tariffs as tariff, i}
								<ListItem
									class={i + 1 === chartStatistic.tariffs.length ? 'mb-s' : ''}
									icon={'fas fa-coins'}
									title={tariff.name}
									extraText={{
										title: `${tariff.consumed}`,
										color: 'primary',
										icon: 'fas fa-bolt',
										iconColor: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,
									}}
									progress={{
										title: ` ${tariff.cost}`,
										color: 'warning',
										icon: 'fas fa-coins',
										iconColor: SystemStyles.COLOR_CHART_MONEY_VALUE,
									}}
								/>
							{/each}
						</Stack>
					</TransitionWrapper>
				</div>
			{/if}
		{/if}
		{#if chartStatistic.totalCounter}
			<ListItem
				icon={'fas fa-plus'}
				title={capFirstChar(
					$format('chart_legend_total', {
						default: 'Total',
					}),
				)}
				extraText={{
					title: chartStatistic.totalCounter,
					color: 'primary',
				}}
			/>
		{/if}
		{#if chartStatistic.totalConsumedValue > 0 || chartStatistic?.statistic}
			<ListItem
				icon={'fas fa-percent'}
				title={capFirstChar(
					$format('average', {
						default: 'average',
					}),
				)}
				extraText={{
					title: `${chartStatistic.average}`,
					color: 'primary',
					icon: chartStatistic.name == 'consumption' ? 'fas fa-bolt' : '',
					iconColor: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,
				}}
				progress={{
					title: `${chartStatistic.averageCost ? chartStatistic.averageCost : ''}`,
					color: 'warning',
					icon: `${chartStatistic.averageCost ? 'fas fa-coins' : ''}`,
					iconColor: SystemStyles.COLOR_CHART_MONEY_VALUE,
				}}
			/>
			<ListItem
				icon={'fas fa-arrow-up-to-line'}
				title={capFirstChar(
					$format('max', {
						default: 'max',
					}),
				)}
				extraText={{
					title: `${chartStatistic.max}`,
					color: 'primary',
					icon: chartStatistic.name == 'consumption' ? 'fas fa-bolt' : '',
					iconColor: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,
				}}
				progress={{
					title: `${chartStatistic.maxCost ? chartStatistic.maxCost : ''}`,
					color: 'warning',
					icon: `${chartStatistic.maxCost ? 'fas fa-coins' : ''}`,
					iconColor: SystemStyles.COLOR_CHART_MONEY_VALUE,
				}}
			/>
			<ListItem
				icon={'fas fa-arrow-down-to-line'}
				title={capFirstChar(
					$format('min', {
						default: 'min',
					}),
				)}
				extraText={{
					title: `${chartStatistic.min}`,
					color: 'primary',
					icon: chartStatistic.name == 'consumption' ? 'fas fa-bolt' : '',
					iconColor: SystemStyles.GRADIENT_CHART_CONSUMED_ENERGY,
				}}
				progress={{
					title: `${chartStatistic.minCost ? chartStatistic.minCost : ''}`,
					color: 'warning',
					icon: `${chartStatistic.minCost ? 'fas fa-coins' : ''}`,
					iconColor: SystemStyles.COLOR_CHART_MONEY_VALUE,
				}}
			/>
		{/if}
		{#if chartStatistic.totalGeneratedValue > 0}
			<ListItem
				icon={'fas fa-percent'}
				title={capFirstChar(
					$format('label_returned_average', {
						default: 'Average Returned',
					}),
				)}
				progress={{
					title: `${chartStatistic.returnedAverage}`,
					color: 'solar',
					icon: 'fas fa-bolt',
					iconColor: SystemStyles.COLOR_SOLAR,
				}}
			/>
			<ListItem
				icon={'fas fa-arrow-up-to-line'}
				title={capFirstChar(
					$format('label_returned_max', {
						default: 'Max Returned',
					}),
				)}
				progress={{
					title: `${chartStatistic.returnedMin}`,
					color: 'solar',
					icon: 'fas fa-bolt',
					iconColor: SystemStyles.COLOR_SOLAR,
				}}
			/>
			<ListItem
				icon={'fas fa-arrow-down-to-line'}
				title={capFirstChar(
					$format('label_returned_min', {
						default: 'Min Returned',
					}),
				)}
				progress={{
					title: `${chartStatistic.returnedMax}`,
					color: 'solar',
					icon: 'fas fa-bolt',
					iconColor: SystemStyles.COLOR_SOLAR,
				}}
			/>
		{/if}
	</Stack>
{/if}
